import * as pdfMake from 'pdfmake/build/pdfmake';
import { Component, inject, Injectable, signal } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog } from "@angular/material/dialog";
import { lastValueFrom } from "rxjs";
import {
    BaseDialogService,
    NonNullableConfigData,
    TilbyDialogContentComponent,
    TilbyDialogToolbarComponent
} from "@tilby/tilby-ui-lib/components/tilby-dialog";
import { documentPrinter, restManager } from "app/ajs-upgraded-providers";
import { ConfigurationManagerService, EnvironmentInfoService, SystemUtilsService } from "src/app/core";
import { ObjDataData, ReducePrinter } from "../../features/dashboard/models/dashboard.model";
import { AlertDialogService } from "../alert-dialog";
import { OpenDialogsService } from "../services/open-dialogs.service";
import { PrinterErrorFiscalDialogService } from "../printer-error-fiscal-dialog";
import { DashboardReportGeneratorService } from "src/app/features/dashboard";
import { FiscalGridComponent } from "./fiscal-grid";

const FILENAME = 'Riepilogo';

@Component({
    standalone: true,
    selector: 'app-fiscal-printer-dashboard-dialog',
    templateUrl: './fiscal-printer-dashboard-dialog.component.html',
    imports: [
        TilbyDialogToolbarComponent,
        TilbyDialogContentComponent,
        FiscalGridComponent
    ]
})
export class FiscalPrinterDashboardDialogComponent {
    private readonly alertDialogService = inject(AlertDialogService);
    private readonly configurationManagerService = inject(ConfigurationManagerService);
    private readonly dashboardReportGeneratorService = inject(DashboardReportGeneratorService);
    private readonly documentPrinter = inject(documentPrinter);
    private readonly environmentInfoService = inject(EnvironmentInfoService);
    private readonly openDialogsService = inject(OpenDialogsService);
    private readonly printerErrorFiscalService = inject(PrinterErrorFiscalDialogService);
    private readonly restManagerService = inject(restManager);
    private readonly systemUtilsService = inject(SystemUtilsService);
    protected readonly data: ObjDataData = inject(MAT_DIALOG_DATA);

    protected readonly customActions = [
        {
            isIt: signal(this.environmentInfoService.canShare()),
            name: '',
            icon: signal('share'),
            click: () => this.shareDetails()
        },
        {
            isIt: signal(true),
            name: '',
            icon: signal('print'),
            click: () => this.printDetails()
        },
        {
            isIt: signal(true),
            name: '',
            icon: signal('email'),
            click: () => this.emailDetails()
        },
        {
            isIt: signal(this.environmentInfoService.canDownloadFiles()),
            name: '',
            icon: signal('download'),
            click: () => this.downloadDetails()
        }
    ];
    private printInProgress = false;

    private async shareDetails() {
        if (!this.environmentInfoService.canShare()) {
            return;
        }

        const pdf = await this.getPdf();
        const dataUri = await pdf.getBase64();

        this.systemUtilsService.share({
            title: FILENAME,
            text: FILENAME,
            files: [`data:application/pdf;base64,${dataUri}`],
        });
    }

    private async printDetails() {
        if (!this.configurationManagerService.isModuleEnabled('cashregister')) {
            await this.alertDialogService.openDialog({ data: { messageLabel: "DASHBOARD.DIALOG.NO_CASHREGISTER_MODULE" } });
            return;
        }

        if (this.printInProgress) {
            return;
        }

        const document = await this.generateReport();
        const printer = await this.openDialogsService.openGeneralDocumentPrinterSelectorDialog({ data: { titleLabel: 'APPLICATION.ORDER_PRINTER.TITLE' } });
        const printerId = printer?.id;

        if (!printerId) {
            return;
        }

        this.printInProgress = true;

        try {
            await this.documentPrinter.printFreeNonFiscal(document, printerId);
        } catch (error: any) {
            this.printerErrorFiscalService.openDialog({ data: { error, options: { printerId } } });
        } finally {
            this.printInProgress = false;
        }
    }

    private async emailDetails() {
        const pdf = await this.getPdf();
        const buffer = await pdf.getBuffer();

        this.openDialogsService.openEmailExportDialog({
            data: {
                messageLabel: 'DASHBOARD.EXPORT.SEND_VIA_EMAIL',
                confirmLabel: 'DIALOG.EMAIL.ACTION.CONFIRM',
                cancelLabel: 'DIALOG.EMAIL.ACTION.CANCEL'
            }
        }).then(res => {
            const mailAddress = res?.email;

            if (!mailAddress) {
                return;
            }

            this.restManagerService.postOne("utilities/send_document_via_email", buffer, {
                email: mailAddress,
                filename: FILENAME,
            }, { "content-type": "application/pdf" });
        });
    }

    private async downloadDetails() {
        const pdf = await this.getPdf();

        await pdf.download(FILENAME);
    }

    private generateReport() {
        return this.dashboardReportGeneratorService.generateReport(<ReducePrinter[]>this.data.printers, this.data.today);
    }

    private async getPdf() {
        const document = await this.generateReport();

        return pdfMake.createPdf({
            content: [document],
            defaultStyle: {
                font: 'Monospace',
                fontSize: 10
            }
        });
    }
}

@Injectable({
    providedIn: 'root',
})
export class FiscalPrinterDashboardDialogService extends BaseDialogService {
    private readonly dialog = inject(MatDialog);

    public openDialog(config: NonNullableConfigData<ObjDataData>): Promise<any> {
        const dialogConfig: NonNullableConfigData<ObjDataData> = {
            ...this.switchMobileDesktopDimensions(),
            ...config
        };

        return lastValueFrom(this.dialog.open(FiscalPrinterDashboardDialogComponent, dialogConfig).afterClosed());
    }
}
